import { LegalScopeNotice } from "@/components/LegalScopeNotice";
import { Link } from "wouter";
import { SEARCH_AI_NOTICE } from "@/lib/searchNotice";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BackToHomeLink } from "@/components/BackToHomeLink";

export default function PrivacyPolicy() {
  usePageMeta({
    title: "Privacy Policy",
    description: "How FreeDailyPro collects, uses, and protects your data.",
    path: "/privacy",
  });

  return (
    <div className="container max-w-3xl mx-auto py-12 px-4">
      <BackToHomeLink />
      <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Last updated: October 14, 2026
      </p>

      <LegalScopeNotice />

      <div className="prose prose-sm max-w-none space-y-8 text-sm leading-relaxed text-foreground/90">
        <section>
          <p>
            FreeDailyPro ("we," "us," or "our") is operated by DreamUSA LLC.
            This Privacy Policy explains what information we collect when you
            use freedailypro.com (the "Service"), how we use it, and the choices
            you have.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Information We Collect</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Account information:</strong> When you sign in with
              Google, we receive your name, email address, and a unique Google
              account identifier. We do not receive or store your Google
              password. You can also sign in with a one-time email link instead,
              which never involves a password.
            </li>
            <li>
              <strong>Newsletter address:</strong> If you subscribe to our
              monthly note, we keep your email address (and nothing else) on
              our email provider's mailing list, only after you confirm it from
              the link we email you. Every issue has an unsubscribe link, and
              unsubscribing stops all further mail. This is the one place we
              keep an address for someone who has not made an account.
            </li>
            <li>
              <strong>Payment information:</strong> If you purchase a Day Pass
              or Monthly Pro subscription, payment is processed entirely by
              Stripe. For paid plans, we use your account email address only
              to process that payment and manage your subscription — renewal,
              receipts, and support related to your plan — never for
              marketing. We never see or store your full card number — we
              only receive a confirmation of payment and a Stripe-generated
              customer/subscription ID to manage your access.
            </li>
            <li>
              <strong>Usage data:</strong> We record which tools you use and
              when, tied to your account, so we can enforce the daily trial usage
              limit and recognize Pro access correctly.
            </li>
            <li>
              <strong>Search entries:</strong> What you type into the search
              box is stored, together with how well our search matched it, which
              result you clicked first, and either an anonymous random device ID kept in your browser
              or your account if you are signed in. We use this only to
              improve our search. {SEARCH_AI_NOTICE}
            </li>
            <li>
              <strong>Your files:</strong> For the large majority of our
              tools (PDF, image, text, converters and similar), files are
              processed entirely in your browser and are not uploaded to or
              stored on our servers. A few features send data to our servers
              to work, and say so on their own page: the Book Writing Toolbox
              and developer API when you use their server-side features, and
              "Check a live page" in the Open Graph preview tool, which sends
              the page address you type so our server can fetch that public
              page's tags. We do not keep the files or addresses after the
              request finishes.
            </li>
            <li>
              <strong>Cookies and similar technology:</strong> We use a session
              cookie to keep you signed in (strictly necessary). Google
              Analytics and Google AdSense cookies are optional: in the EEA,
              UK and Switzerland we load them only after you choose "Accept
              all" in the cookie banner, and you can change or withdraw your
              choice at any time from "Cookie settings" in the site footer.
              Elsewhere they load by default, and you can still open Cookie
              settings to turn them off.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">How We Use Information</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>To provide, maintain, and secure the Service</li>
            <li>To authenticate you and keep your account signed in</li>
            <li>To enforce daily trial usage limits and recognize paid Pro access</li>
            <li>To process payments and manage subscriptions</li>
            <li>To respond to support requests sent to us</li>
            <li>
              To understand aggregate usage trends and improve the Service
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Third-Party Services</h2>
          <p className="mb-2">
            We rely on the following third parties, each with their own privacy
            practices:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Google Sign-In</strong> — for authentication.{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Google's Privacy Policy
              </a>
            </li>
            <li>
              <strong>Resend</strong> — sends the one-time sign-in link if you
              choose email sign-in instead of Google.{" "}
              <a
                href="https://resend.com/legal/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Resend's Privacy Policy
              </a>
            </li>
            <li>
              <strong>Cloudflare and Groq</strong> — only for complex or
              advanced searches: the text of your search and a short list of
              tool names are sent to an AI language model (hosted on
              Cloudflare Workers and run by Groq) to improve the results.
              Never your files. If these services are unavailable or blocked,
              search falls back to standard results.{" "}
              <a
                href="https://www.cloudflare.com/privacypolicy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Cloudflare's Privacy Policy
              </a>
              {" · "}
              <a
                href="https://groq.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Groq's Privacy Policy
              </a>
            </li>
            <li>
              <strong>Stripe</strong> — for payment processing.{" "}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Stripe's Privacy Policy
              </a>
            </li>
            <li>
              <strong>Google Analytics</strong> — for aggregate, anonymized
              usage statistics.
            </li>
            <li>
              <strong>Google AdSense</strong> — to show ads to users on the trial tier
              (Pro subscribers do not see ads). AdSense may use cookies to
              personalize ads based on your browsing activity. You can opt out
              of personalized advertising at{" "}
              <a
                href="https://adssettings.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Google Ads Settings
              </a>
              .
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Data Retention</h2>
          <p>
            We retain account and usage data for as long as your account is
            active. You can delete your account and its associated data
            yourself at any time from your{" "}
            <Link href="/profile" className="text-primary underline">
              Profile page
            </Link>{" "}
            — this requires no active paid plan on the account, so cancel
            your subscription first if you have one. Deletion is immediate
            and can't be undone. Search entries linked to your account are
            deleted with it; entries from signed-out visitors are tied only to
            a random device ID, not to a person.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Event Suite Premium: guest messaging</h2>
          <p>
            If an organiser on Event Suite Premium invites guests, we keep, for
            up to 180 days, the guest's name, email address, their RSVP answer
            and how they chose to get reminders. A guest who chooses text
            reminders also gives a mobile number and agrees on the RSVP page.
            Texts are sent through the organiser's own Twilio account and
            number; we store that account's restricted API key encrypted and
            never ask for the Twilio password or Auth Token. Guests can reply
            STOP to any text to stop. All of this is erased when the
            organiser's Premium plan ends or their account is deleted.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Event Suite Premium: event apps</h2>
          <p>
            When an organiser publishes an event, we keep the event details,
            programme and announcements they choose to publish. Door staff
            links also carry the guest list (names, ticket type and status)
            and the check-ins made at the door. Each exhibitor's Booth Leads
            link keeps the leads that exhibitor captures; the organiser sees
            only how many there are. A guest who uses the Guest app keeps their
            pass on their own phone; we do not learn which guest opened a link.
            This is kept until the organiser deletes it or their Premium plan
            ends, and counts toward their 1 GB of storage. It is erased when
            Premium ends or the account is deleted. Organisers can stop any
            link at any time.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Your Choices and Rights</h2>
          <p className="mb-2">
            Depending on where you live (for example the EEA, UK, or
            California), you may have the right to access, correct, delete, or
            export your personal data, to object to or restrict certain
            processing, and to withdraw consent. We do not sell your personal
            information. Email us to exercise any of these rights; you can
            also delete your account yourself from your Profile page.
          </p>
          <p>
            You can request access to, correction of, or deletion of your
            personal data at any time by contacting us. You can also revoke
            FreeDailyPro's access to your Google account at any time via your{" "}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Google Account permissions page
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Children's Privacy</h2>
          <p>
            The Service is not directed to children under 13, and we do not
            knowingly collect personal information from children under 13.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Changes to This Policy</h2>
          <p>
            We may update this policy from time to time. Material changes will
            be reflected by updating the "Last updated" date above.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Contact Us</h2>
          <p>
            Questions about this policy? Email us at{" "}
            <a
              href="mailto:support@freedailypro.com"
              className="text-primary underline"
            >
              support@freedailypro.com
            </a>{" "}
            or visit our{" "}
            <Link href="/contact" className="text-primary underline">
              Contact page
            </Link>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
